import "zod/compile";
import { z } from "zod";
import { JsonWireValueSchema } from "./jsonValue";
import type { SchemaFieldType } from "./schemaDefinitions";

const FieldIdSchema = z
  .string()
  .min(1)
  .refine((id) => id === id.trim(), {
    message: "Field IDs must not have surrounding whitespace",
  });

/** The server resolves these identities through its trusted table registry. */
export const ExtensionColumnSelectionSchema = z.strictObject({
  extensionId: z.string().uuid(),
  fieldId: FieldIdSchema,
  relation: z
    .string()
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/)
    .optional(),
});

export const ExtensionColumnSelectionsSchema = z.array(
  ExtensionColumnSelectionSchema,
);

export type ExtensionColumnSelection = z.infer<
  typeof ExtensionColumnSelectionSchema
>;

/** Immediate presentation policy, stored on the extension, outside snapshots. */
export const ExtensionFieldExposureSchema = z.strictObject({
  detail: z.boolean(),
  columns: z.boolean(),
  filter: z.boolean(),
  agent: z.boolean(),
  agentWrite: z.boolean(),
});

export const ExtensionFieldExposureMapSchema = z.record(
  FieldIdSchema,
  ExtensionFieldExposureSchema,
);

export type ExtensionFieldExposure = z.infer<
  typeof ExtensionFieldExposureSchema
>;
export type ExtensionFieldExposureMap = z.infer<
  typeof ExtensionFieldExposureMapSchema
>;

/** Missing entries grant no exposure, including names inherited from Object. */
export function getExtensionFieldExposure(
  exposures: ExtensionFieldExposureMap,
  fieldId: string,
): ExtensionFieldExposure {
  if (Object.prototype.hasOwnProperty.call(exposures, fieldId)) {
    return exposures[fieldId];
  }
  return {
    detail: false,
    columns: false,
    filter: false,
    agent: false,
    agentWrite: false,
  };
}

/** JSON-safe wire values. Each field's codec applies stricter type validation. */
export const ExtensionValueSchema = JsonWireValueSchema;
export type ExtensionValue = z.infer<typeof ExtensionValueSchema>;

const DecimalStringSchema = z.string().regex(/^-?\d+(?:\.\d+)?$/);
const SafeIntegerSchema = z.number().int().safe();
const IntegerStringSchema = z.string().regex(/^-?(?:0|[1-9]\d*)$/);
const UnsafeIntegerStringSchema = IntegerStringSchema.refine(
  (value) => !Number.isSafeInteger(Number(value)),
  { message: "Safe integers use numbers on the wire" },
);

/** Never recover an exact decimal from a fractional or unsafe JS number. */
export const ExtensionDecimalCodec = z.codec(
  z.union([DecimalStringSchema, SafeIntegerSchema]),
  DecimalStringSchema,
  { decode: (value) => String(value), encode: (value) => value },
);

export const ExtensionIntegerCodec = z.codec(
  z.union([SafeIntegerSchema, IntegerStringSchema]),
  z.union([SafeIntegerSchema, UnsafeIntegerStringSchema]),
  {
    decode: (value) => {
      const number = Number(value);
      return Number.isSafeInteger(number) ? number : value;
    },
    encode: (value) => value,
  },
);

export const ExtensionDatetimeCodec = z.codec(
  z.union([z.date(), z.iso.datetime({ offset: true })]),
  z.iso.datetime(),
  {
    decode: (value) => new Date(value).toISOString(),
    encode: (value) => new Date(value),
  },
);

const TextValueSchema = z.string().nullable();
const JsonValueSchema = ExtensionValueSchema;

/** Exhaustive so adding a field type requires an explicit transport decision. */
export const ExtensionValueCodecs = {
  string: TextValueSchema,
  text: TextValueSchema,
  enum: TextValueSchema,
  file: TextValueSchema,
  image: TextValueSchema,
  url: TextValueSchema,
  time: z.iso.time().nullable(),
  integer: ExtensionIntegerCodec.nullable(),
  numeric: ExtensionDecimalCodec.nullable(),
  currency: ExtensionDecimalCodec.nullable(),
  float: z.number().finite().nullable(),
  boolean: z.boolean().nullable(),
  date: z.iso.date().nullable(),
  datetime: ExtensionDatetimeCodec.nullable(),
  json: JsonValueSchema,
  range: JsonValueSchema,
  geospatial: JsonValueSchema,
  unit: JsonValueSchema,
  object: z.record(z.string(), ExtensionValueSchema).nullable(),
  array: z.array(ExtensionValueSchema).nullable(),
  entity_reference: JsonValueSchema,
  document: JsonValueSchema,
} satisfies Record<SchemaFieldType, z.ZodType<ExtensionValue>>;

/** Decode an untrusted database value; invalid values fail instead of coercing. */
export function decodeExtensionValue(
  fieldType: SchemaFieldType,
  value: unknown,
): ExtensionValue {
  return ExtensionValueCodecs[fieldType].parse(value);
}

export const ExtensionFieldTypeSchema = z.custom<SchemaFieldType>(
  (value) =>
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(ExtensionValueCodecs, value),
  { message: "Unknown extension field type" },
);

export const ExtensionColumnMetadataSchema =
  ExtensionColumnSelectionSchema.extend({
    fieldKey: z.string().optional(),
    extensionName: z.string().optional(),
    label: z.string().optional(),
    fieldType: ExtensionFieldTypeSchema.optional(),
    detail: z.boolean(),
    issues: z.array(
      z.strictObject({
        code: z.enum([
          "unavailable",
          "unsupported_relation",
          "not_exposed",
          "invalid_value",
        ]),
        entityKey: z.string().optional(),
      }),
    ),
  });
export const ExtensionColumnsMetadataSchema = z.array(
  ExtensionColumnMetadataSchema,
);
export type ExtensionColumnMetadata = z.infer<
  typeof ExtensionColumnMetadataSchema
>;
